import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const STEPH =
  "/media/photo-stephanie.jpg";
const SUNSET = "/media/photo-soleil.jpg";
const DEST = "/media/destinations.webp";

export default function EcolePublic() {
  const { tr } = useLang();
  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="ecole-page-public">
      <div className="relative overflow-hidden bg-deep/50 pb-24 pt-14">
        <div className="absolute inset-0 caustic-glow" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <SectionLabel>{tr("L'école — OJANZU")}</SectionLabel>
                <h1 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
                  {tr("Une transmission authentique & certifiée")}
                </h1>
                <p className="mt-7 max-w-xl text-sm md:text-base leading-relaxed text-foam/65">
                  {tr("Fondée par Stéphanie, l'École Française de Janzu transmet un soin né des techniques créées par Juan Villatoro Garza dans les années 1980. Le cursus progressif mène du premier contact avec l'eau au titre de Praticien·ne Expert·e, avec un enseignement en bassin privé chauffé à environ 33 °C.")}
                </p>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foam/65">
                  {tr("Certifiée Qualiopi, l'école propose des sessions en Charente-Maritime, sur l'île de La Réunion et à Maurice, avec des dates modulables pour s'adapter à votre parcours.")}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-9 flex flex-wrap gap-3">
                  {[
                    "Avoir reçu au moins une séance de Janzu",
                    "18 ans minimum",
                    "Être apte à se mouvoir librement",
                  ].map((c) => (
                    <span key={c} className="inline-flex items-center gap-2 rounded-full border border-aqua/25 bg-abyss/60 px-4 py-2 text-xs text-foam/80">
                      <Check size={13} className="text-aqua" /> {tr(c)}
                    </span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/certification"
                    data-testid="ecole-certification-link"
                    className="rounded-full bg-aqua px-7 py-3.5 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)]"
                  >
                    {tr("Notre certification Qualiopi")}
                  </Link>
                  <Link
                    to="/formations"
                    data-testid="ecole-formations-link"
                    className="rounded-full border border-foam/25 px-7 py-3.5 text-sm font-medium text-foam/85 transition-colors duration-300 hover:border-foam/60 hover:text-foam"
                  >
                    {tr("Voir les formations")}
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="space-y-3">
                <div className="img-clip-b overflow-hidden">
                  <img
                    src={STEPH}
                    alt="Stéphanie Vautey, directrice de l'école OJANZU, salue le groupe dans le lagon"
                    data-testid="ecole-photo-stephanie"
                    loading="lazy"
                    className="h-[380px] w-full object-cover [object-position:center_20%] md:h-[440px]"
                  />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-foam/60">
                  {tr("Stéphanie Vautey — Directrice & formatrice")}
                </p>
                <div className="img-clip-a overflow-hidden">
                  <img
                    src={SUNSET}
                    alt="Stéphanie dans l'eau au coucher du soleil — Amour de l'eau et de la vie"
                    loading="lazy"
                    className="h-[220px] w-full object-cover md:h-[260px]"
                  />
                </div>
                <div className="img-clip-b overflow-hidden">
                  <img
                    src={DEST}
                    alt="France Métropolitaine, La Réunion, Île Maurice, Zanzibar — Vivre la magie de l'eau"
                    data-testid="ecole-photo-destinations"
                    loading="lazy"
                    className="h-[480px] w-full object-cover [object-position:center_32%] md:h-[560px]"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
